import type { ModalIbanAccountStep, QuizStepProps } from "@/features/user/quiz/types.ts";
import { useTranslation } from "react-i18next";
import { Base, Info, SectionTitleSmall } from "@/atoms-react/text/Text.react.tsx";
import InputLabel from "@/atoms-react/input/Input.react.tsx";
import DotsNavigation from "@/atoms-react/dots-navigation/DotsNavigation.react.tsx";
import { Button } from "@/atoms-react/button/Button.react.tsx";
import { Themes } from "@/types/theme.ts";
import { useState } from "react";
import { RadioCard } from "@/atoms-react/radio-card/index.react.tsx";
import toast from "react-hot-toast";
import useCreateIban from "@/features/user/iban/hooks/use-create-iban";
enum ModalRadiosTransKeys {
  YesCommunicate = "yes_communicate",
  YesNotCommunicate = "yes_not_communicate",
  Nope = "no"
}
export const EuropeanAccountStep = ({
  setStep,
  quizResponses,
  setQuizResponses,
  stepSettings,
  setPrevStep,
  totalSteps = 8,
  handleSuccess,
  handleIbanUuid,
  ibanVerified,
  handleRefetchUser
}: QuizStepProps & {
  handleIbanUuid: (uuidIban: string) => void;
  handleSuccess: () => Promise<void>;
  ibanVerified?: string;
  handleRefetchUser: () => Promise<void>;
}) => {
  const { t } = useTranslation(["pass"]);
  const [loading, setLoading] = useState(false);

  const { mutateAsync: createIban } = useCreateIban();
  const handleRadioBoxChange = (selectedOption: string) =>
    setQuizResponses?.((prev) => ({
      ...prev,
      european_account: { ...prev.european_account, trans_key: selectedOption }
    }));

  const handleIbanChange = (iban: string) =>
    setQuizResponses?.((prev) => ({
      ...prev,
      european_account: { trans_key: prev.european_account.trans_key, iban }
    }));
  const isQuizAnswered = () => {
    if (
      !quizResponses?.european_account ||
      Object.keys(quizResponses?.european_account).length === 0
    ) {
      return false;
    }
    if (quizResponses?.european_account?.trans_key === null) {
      return false;
    }
    if (
      quizResponses?.european_account?.trans_key === ModalRadiosTransKeys.YesCommunicate &&
      (!quizResponses?.european_account?.iban ||
        quizResponses?.european_account?.iban?.trim() === "")
    ) {
      return false;
    }
    return true;
  };
  const handleOnClickNext = async () => {
    try {
      if (quizResponses?.european_account?.trans_key !== ModalRadiosTransKeys.YesCommunicate) {
        setStep(stepSettings.condition_next as ModalIbanAccountStep);
      } else {
        setLoading(true);
        if (ibanVerified && quizResponses.european_account.iban === ibanVerified) {
          handleSuccess();
          setLoading(false);
          return;
        }

        const response = await createIban({
          iban: quizResponses?.european_account?.iban as string,
          requestValidate: true,
          main: false
        });
        await handleRefetchUser();
        if (response?.data?.uuid) {
          if (response?.data?.valid === false) {
            toast.error(
              t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.european_account.error")
            );
            setLoading(false);
            return;
          }
          if (response.data.valid) {
            handleSuccess();
            setLoading(false);
            return;
          }
          handleIbanUuid(response.data?.uuid);
          setStep(stepSettings.next as ModalIbanAccountStep);
        } else {
          toast.error(t("olkypass.accountPage.notifications.error.iban.create"));
        }
        setLoading(false);
      }
    } catch (_e) {
      setLoading(false);
      toast.error(t("olkypass.accountPage.notifications.error.iban.create"));
    }
  };
  return (
    <>
      <SectionTitleSmall>
        {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.european_account.question")}
      </SectionTitleSmall>
      <Info className="mt-2 mb-4 block">
        {t(
          "olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.european_account.helpers.european_countries"
        )}
      </Info>
      <div className="my-4 mt-auto w-full space-y-4">
        {[
          ModalRadiosTransKeys.YesCommunicate,
          ModalRadiosTransKeys.YesNotCommunicate,
          ModalRadiosTransKeys.Nope
        ].map((m) => (
          <RadioCard
            active={
              (quizResponses?.european_account &&
                quizResponses?.european_account?.trans_key === m) as boolean
            }
            dataCy={`european_account-radio`}
            key={m}
            onClick={() => handleRadioBoxChange(m)}
          >
            <Base>
              {t(
                "olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.european_account.responses." + m
              )}
            </Base>
            {quizResponses?.european_account &&
              quizResponses?.european_account?.trans_key === m && (
                <Info className="text-sky-500 dark:text-sky-500">
                  {t(
                    "olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.european_account.helpers." +
                      m
                  )}
                </Info>
              )}
            {quizResponses?.european_account &&
              quizResponses?.european_account?.trans_key === m &&
              m === ModalRadiosTransKeys.YesCommunicate && (
                <div className={"mt-2 mb-4"}>
                  <InputLabel
                    label=""
                    value={quizResponses?.european_account?.iban as string}
                    onChange={(value: string) => handleIbanChange(value)}
                    className="mb-1 flex-1 md:mb-0"
                  />
                </div>
              )}
          </RadioCard>
        ))}
      </div>
      <div className="mt-auto mb-4 flex flex-col items-center">
        <DotsNavigation
          total={
            quizResponses?.account_usage?.trans_key == "perso_pro" ? totalSteps + 3 : totalSteps
          }
          current={stepSettings.current}
          className="mb-4"
        />
        <div className="flex w-full flex-col-reverse gap-y-2 md:flex-row md:gap-x-2">
          <Button
            theme={Themes.white}
            onClick={() => setPrevStep(stepSettings.previous as ModalIbanAccountStep)}
          >
            {t("olkypass.general.go_back")}
          </Button>

          <Button
            disabled={!isQuizAnswered()}
            loading={loading}
            onClick={() => handleOnClickNext()}
            dataCy={`european_account-step-continue`}
          >
            {t("olkypass.general.continue")}
          </Button>
        </div>
      </div>
    </>
  );
};
